import {
  API_BUDGET_TEMPLATE,
  API_BUDGET_TEMPLATE_AI_EXTRACT,
  API_BUDGET_TEMPLATE_APPLY_TEMPLATE,
  API_BUDGET_TEMPLATE_BORROWER_CONFIRM,
  API_BUDGET_TEMPLATE_BY_ID,
  API_BUDGET_TEMPLATE_EXPORT_QA,
  API_BUDGET_TEMPLATE_ITEM,
  API_BUDGET_TEMPLATE_LOCK_BORROWER,
  API_BUDGET_TEMPLATE_PARSE_EXCEL,
  API_BUDGET_TEMPLATE_SEND_TO_BORROWER,
  API_BUDGET_TEMPLATE_SIGN_SIGNATURE,
  API_BUDGET_TEMPLATE_SITEWIRE,
  API_BUDGET_TEMPLATE_TITLE,
  API_PARSE_FULL_EXCEL,
} from 'config'
import Api from 'services/api'

import { downloadFile, fileJsonConvert } from '../documents'

export const getBudgetTemplateList = () => {
  return Api.get(API_BUDGET_TEMPLATE)
}

export const createBudgetTemplateList = (title = '') => {
  return Api.post(API_BUDGET_TEMPLATE, title ? { title } : {})
}

/** Name a budget form. A blank name puts its tab back to the property address. */
export const updateBudgetTemplateTitle = (id: number, title: string) => {
  return Api.put(API_BUDGET_TEMPLATE_TITLE, { title }, { id })
}

export const getBudgetTemplate = (id: number) => {
  return Api.get(API_BUDGET_TEMPLATE_BY_ID, {}, { id })
}

export const updateBudgetTemplate = (id: number, data: Record<string, any>) => {
  return Api.put(API_BUDGET_TEMPLATE_BY_ID, data, { id })
}

export const deleteBudgetTemplate = (id: number) => {
  return Api.delete(API_BUDGET_TEMPLATE_BY_ID, {}, { id })
}

export const updateBudgetTemplateItem = (id: number, data: Record<string, any>) => {
  return Api.put(API_BUDGET_TEMPLATE_ITEM, data, { id })
}

/** Rebuild a budget form's line items from a different configured budget template. */
export const applyBudgetTemplate = (
  id: number,
  templateKey: string,
): Promise<{ success: boolean; templateKey: string; carriedOver: number; dropped: number }> => {
  return Api.put(API_BUDGET_TEMPLATE_APPLY_TEMPLATE, { templateKey }, { id })
}

export const borrowerConfirmBudget = (id: number) => {
  return Api.get(API_BUDGET_TEMPLATE_BORROWER_CONFIRM, {}, { id }, { timeout: 150000 })
}

export const updateLockBorrower = (id: number, value: boolean) => {
  return Api.put(API_BUDGET_TEMPLATE_LOCK_BORROWER, { value }, { id })
}

export const sendBudgetToBorrower = (id: number, emails: Record<string, string>, note: string) => {
  return Api.post(API_BUDGET_TEMPLATE_SEND_TO_BORROWER, { emails, note }, { id })
}

export const orderSitewire = (id: number) => {
  return Api.post(API_BUDGET_TEMPLATE_SITEWIRE, {}, { id }, { timeout: 60000 })
}

export const validateBudgetSignSignature = (id: number, data: Record<string, any>) => {
  return Api.post(API_BUDGET_TEMPLATE_SIGN_SIGNATURE, data, { id })
}

export const parseBudgetTemplateExcel = (data: any) => {
  const json = fileJsonConvert(data)
  return Api.uploadFiles(API_BUDGET_TEMPLATE_PARSE_EXCEL, json.data, {}, json.files)
}

export const parseFullExcel = (data: any) => {
  const json = fileJsonConvert(data)
  return Api.uploadFiles(API_PARSE_FULL_EXCEL, json.data, {}, json.files)
}

export type BudgetAiExtractItem = {
  sectionValue: string | null
  lineKey: string | null
  title: string | null
  rehabCostBorrowerEstimated: number | null
  rehabCostAlreadyPrepaid: number | null
  costType: string | null
  description: string | null
}

export type BudgetAiExtractUnmatched = {
  title: string | null
  rehabCostBorrowerEstimated: number | null
  rehabCostAlreadyPrepaid: number | null
  costType: string | null
  description: string | null
  /** The heading this line sat under on the document, when it printed its own sections. */
  section?: string | null
}

export type BudgetAiExtractResult = {
  narrative: string | null
  items: BudgetAiExtractItem[]
  unmatched: BudgetAiExtractUnmatched[]
}

export const extractBudgetFromPdf = (
  id: number,
  files: Array<{ fileKey: string; name: string }>,
): Promise<{ success: boolean; message?: string; data?: BudgetAiExtractResult }> => {
  return Api.post(API_BUDGET_TEMPLATE_AI_EXTRACT, { files }, { id }, { timeout: 0 })
}

export const downloadBudgetQA = async (
  items: Record<string, any>[],
  narrativeNote: string,
  type: string,
  fileName: string,
) => {
  const res: Blob = await Api.post(
    API_BUDGET_TEMPLATE_EXPORT_QA,
    { items, narrativeNote, type },
    {},
    {
      responseType: 'blob',
    },
  )
  downloadFile(fileName, res)
}
